import { useQuery } from 'react-apollo';

import { getTeamTag } from '../../../../../../../../util/album';
import { AlbumFolderTags } from '../../../../../../../../graphql/queries/album-folder-tags';
import { getAlbumMembershipGroupIdentityQuery } from '../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroupIdentity';

/**
 * Hook to retrieve the team photos for a given identity within an
 * AlbumMembershipGroup, by tagging photos with the album folder's team tag.
 *
 * @param {Object} opts the parameters passed
 * @param {String} opts.albumFolderId the album folder id used to fetch the album folder tags
 * @param {String} opts.albumMembershipGroupId the hierarchy item / album membership group id
 * @param {String} opts.identityId the identity id whose matched photos will be retrieved
 *
 * @returns {{ loading: Boolean, teamPhotos: Array, albumFolderTags: Array }}
 */
const useAlbumMembershipGroupTeamPhotos = ({
  albumFolderId,
  albumMembershipGroupId,
  identityId,
}) => {
  const { data: { albumFolder } = {}, loading: albumFolderTagsLoading } =
    useQuery(AlbumFolderTags, {
      variables: { id: albumFolderId },
      skip: !albumFolderId,
    });

  const albumFolderTags = albumFolder?.tags?.records ?? [];
  const teamTag = getTeamTag(albumFolderTags);

  const { data: { albumMembershipGroup } = {}, loading: identityLoading } =
    useQuery(getAlbumMembershipGroupIdentityQuery, {
      variables: {
        id: albumMembershipGroupId,
        identityId,
        album_folder_tag_ids: [teamTag?.id],
      },
      skip: !albumMembershipGroupId || !identityId || !teamTag?.id,
    });

  const canFetchTeamPhotos =
    !!albumFolderId &&
    !!albumMembershipGroupId &&
    !!identityId &&
    !!teamTag?.id;

  const loading =
    albumFolderTagsLoading || (canFetchTeamPhotos && identityLoading);

  const teamPhotos =
    albumMembershipGroup?.identities?.[0]?.matched_photos_extended?.records ??
    [];

  return {
    loading,
    teamPhotos,
    albumFolderTags,
  };
};

export default useAlbumMembershipGroupTeamPhotos;
